/**
 * 權限管理工具函數
 * 處理用戶權限檢查和介面過濾
 */

// 介面層級關係定義
// 注意：phoneVerificationAdmin 不包含在 adminTools 的自動展開中，因為只有 Tenant_Admin 應該有這個權限
// 上層打勾 = 下層都算有權限（含 commerce 及其子項）
export const INTERFACE_HIERARCHY = {
  'application': ['publishedApps', 'pendingTasks', 'workflowMonitor', 'chatBotInbox', 'calendar', 'documentLibrary'],
  'studio': ['buildApp', 'eformList', 'whatsappTemplates', 'whatsappWorkflow', 'dataSets', 'aiEmployeeProfiles', 'knowledgeBase', 'workflowPackage', 'reportTemplates'],
  'commerce': ['commerce.customers', 'commerce.priceCards', 'commerce.salesOrders', 'commerce.collections', 'commerce.products', 'commerce.wpaHomePromotions', 'commerce.import', 'commerce.catalogAdmin', 'commerce.wpaPermissions', 'commerce.deliveryRules'],
  'adminTools': ['contactList', 'broadcastGroups', 'hashtags', 'companyUserAdmin', 'permissionManagement', 'apiProviders', 'phoneVerificationAdmin', 'admin.usageBilling.settings'],
  'workflowMonitor': ['workflowMonitor.pause', 'workflowMonitor.resume', 'workflowMonitor.retry', 'workflowMonitor.cancel', 'workflowMonitor.delete'],
  'reports': ['reports.daily', 'reports.monthly', 'reports.realtime', 'reports.appsMedia', 'reports.templateReport', 'reports.eformInstances', 'reports.eformPivot', 'reports.commerce', 'reports.billingSystem'],
  'reports.daily': ['reports.daily.pendingOverview', 'reports.daily.workflowExecution', 'reports.daily.formEfficiency', 'reports.daily.workflowHealth', 'reports.daily.whatsappInteraction'],
  'reports.monthly': ['reports.monthly.workflowPerformance', 'reports.monthly.formApproval', 'reports.monthly.businessInsights', 'reports.monthly.systemUsage', 'reports.monthly.operationalOverview', 'reports.monthly.processStepExecution'],
  'reports.realtime': ['reports.realtime.workflowActivity'],
  'reports.commerce': ['reports.commerce.statement', 'reports.commerce.salesPivot']
  // phoneVerificationAdmin 需要明確授予，不會從 adminTools 自動展開
};

const AUTO_EXPAND_PARENTS = new Set(['application', 'studio', 'commerce', 'adminTools', 'reports']);

// 用於除錯 log：Running Apps 相關權限 key，方便看出來自哪個公司 > 哪個角色
const WORKFLOW_MONITOR_KEYS = [
  'workflowMonitor.pause',
  'workflowMonitor.resume',
  'workflowMonitor.retry',
  'workflowMonitor.cancel',
  'workflowMonitor.delete'
];

// 子 key → 父 key，用於判斷「父是否因上層被勾選而被展開」
function buildParentMap() {
  const parentOf = {};
  Object.keys(INTERFACE_HIERARCHY).forEach(parent => {
    (INTERFACE_HIERARCHY[parent] || []).forEach(child => {
      parentOf[child] = parent;
    });
  });
  return parentOf;
}
const PARENT_OF = buildParentMap();

/**
 * 展開介面權限（有父級權限自動包含子級，有子級權限自動包含父級）
 * @param {string[]} interfaces - 用戶擁有的介面權限列表
 * @param {{ forDisplay?: boolean, forwardOnly?: boolean }} options - forDisplay: 權限頁顯示用，遞迴展開到底；forwardOnly: 僅正向展開（不反向加父），用於權限頁載入，避免只勾子項時父被當成已勾而鎖住子項
 * @returns {string[]} 展開後的介面權限列表
 */
export const expandInterfacesWithChildren = (interfaces, options = {}) => {
  if (!interfaces || !Array.isArray(interfaces)) {
    return [];
  }
  const forDisplay = options.forDisplay === true;
  const forwardOnly = options.forwardOnly === true;
  const expanded = new Set(interfaces);
  const rawSet = new Set(interfaces);

  // 1. 反向展開：有子級則加入父級（forwardOnly 時跳過，這樣只勾子項不會出現「父被當成已勾」而鎖住）
  if (!forwardOnly) {
    interfaces.forEach(iface => {
      if (typeof iface !== 'string') return;
      Object.keys(INTERFACE_HIERARCHY).forEach(parent => {
        const children = INTERFACE_HIERARCHY[parent];
        if (children.includes(iface)) {
          expanded.add(parent);
        }
      });
      if (iface.startsWith('reports.daily.')) {
        expanded.add('reports.daily');
        expanded.add('reports');
      } else if (iface.startsWith('reports.monthly.')) {
        expanded.add('reports.monthly');
        expanded.add('reports');
      } else if (iface.startsWith('reports.realtime.')) {
        expanded.add('reports.realtime');
        expanded.add('reports');
      } else if (iface === 'reports.daily' || iface === 'reports.monthly' || iface === 'reports.realtime' || iface === 'reports.appsMedia' || iface === 'reports.templateReport' || iface === 'reports.eformInstances' || iface === 'reports.eformPivot' || iface === 'reports.commerce' || iface === 'reports.billingSystem') {
        expanded.add('reports');
      } else if (iface.startsWith('reports.commerce.')) {
        expanded.add('reports.commerce');
        expanded.add('reports');
      } else if (iface === 'admin.usageBilling.settings') {
        expanded.add('adminTools');
      }
    });
    // 反向展開時需遞迴加入「祖先」：例如只勾 workflowMonitor.delete 時已加入 workflowMonitor，還需加入 application 才能顯示 Application 主選單
    let ancestorChanged = true;
    while (ancestorChanged) {
      ancestorChanged = false;
      expanded.forEach(key => {
        const parent = PARENT_OF[key];
        if (parent && !expanded.has(parent)) {
          expanded.add(parent);
          ancestorChanged = true;
        }
      });
    }
  }

  // 2. 正向展開
  if (forDisplay) {
    let changed = true;
    while (changed) {
      changed = false;
      Object.keys(INTERFACE_HIERARCHY).forEach(parent => {
        if (!expanded.has(parent)) return;
        (INTERFACE_HIERARCHY[parent] || []).forEach(child => {
          if (!expanded.has(child)) {
            expanded.add(child);
            changed = true;
          }
        });
      });
    }
    // 載入顯示時：若後端只回傳子項（例如只存了 commerce 但 API 回傳展開後的 5 個子 key），補回父層以便勾選框正確顯示
    Object.keys(INTERFACE_HIERARCHY).forEach(parent => {
      const children = INTERFACE_HIERARCHY[parent];
      if (!children || !children.length) return;
      if (expanded.has(parent)) return;
      const allChildrenIn = children.every((c) => expanded.has(c));
      if (allChildrenIn) expanded.add(parent);
    });
  } else {
    // 先依 raw 僅展開一層（僅對 AUTO_EXPAND_PARENTS 且出現在 raw 的父）
    Object.keys(INTERFACE_HIERARCHY).forEach(parent => {
      if (AUTO_EXPAND_PARENTS.has(parent) && rawSet.has(parent)) {
        (INTERFACE_HIERARCHY[parent] || []).forEach(child => expanded.add(child));
      }
    });
    // 再多層迭代：父在 rawSet 或 子在 rawSet 或 父的上一層在 rawSet（勾了「應用」整組時要展開流程監控下的按鈕）時才加入子項
    let changed = true;
    while (changed) {
      changed = false;
      Object.keys(INTERFACE_HIERARCHY).forEach(parent => {
        if (!expanded.has(parent)) return;
        const children = INTERFACE_HIERARCHY[parent] || [];
        const parentExplicit = rawSet.has(parent);
        const grandparentInRawSet = PARENT_OF[parent] != null && rawSet.has(PARENT_OF[parent]);
        children.forEach(child => {
          if (expanded.has(child)) return;
          const childExplicit = rawSet.has(child);
          if (parentExplicit || childExplicit || grandparentInRawSet) {
            expanded.add(child);
            changed = true;
          }
        });
      });
    }
  }
  return Array.from(expanded);
};

/**
 * 將展開後的權限列表精簡為最小儲存集：若某父層與其所有子層都在列表中，只保留父層
 * 用於權限管理頁「儲存」時，避免存 38 個 key，取消勾選父層後也能正確寫回
 * 同時會移除「只勾了部分子項」的父層，避免只勾 Published Apps 時存成 application 而誤授與 Running Apps 等權限
 * @param {string[]} interfaces - 已展開的權限列表（如編輯時勾選狀態）
 * @returns {string[]} 精簡後的列表，可寫入 API
 */
export const collapseToMinimalSet = (interfaces) => {
  if (!interfaces || !Array.isArray(interfaces)) {
    return [];
  }
  const originalSet = new Set(interfaces);
  let list = [...interfaces];
  let changed = true;
  while (changed) {
    changed = false;
    const set = new Set(list);
    const toRemove = new Set();
    Object.keys(INTERFACE_HIERARCHY).forEach((parent) => {
      const children = INTERFACE_HIERARCHY[parent];
      if (!children || !children.length) return;
      if (!set.has(parent)) return;
      const allChildrenIn = children.every((c) => set.has(c));
      if (allChildrenIn) {
        children.forEach((c) => toRemove.add(c));
        changed = true;
      }
    });
    list = list.filter((k) => !toRemove.has(k));
  }
  // 移除「僅部分子項被勾選」的父層，只保留真正全選的父層或葉節點，避免只勾第二層時誤授與同層其他權限
  // 必須用「原始」勾選列表判斷是否全選，否則精簡後 list 已不含子項，會誤把「全選父層」當成部分勾選而刪除
  const toRemoveParents = new Set();
  Object.keys(INTERFACE_HIERARCHY).forEach((parent) => {
    const children = INTERFACE_HIERARCHY[parent];
    if (!children || !children.length) return;
    if (!list.includes(parent)) return;
    const allChildrenInOriginal = children.every((c) => originalSet.has(c));
    if (!allChildrenInOriginal) toRemoveParents.add(parent);
  });
  list = list.filter((k) => !toRemoveParents.has(k));
  return list;
};

/**
 * 取得某介面 key 的所有子孫 key（子、孫、…），用於權限頁：取消勾父層時一併從列表移除
 * @param {string} parentKey - 父層介面 key
 * @returns {string[]} 所有子孫 key
 */
export const getDescendants = (parentKey) => {
  const children = INTERFACE_HIERARCHY[parentKey];
  if (!children || !children.length) return [];
  const result = [...children];
  children.forEach((child) => {
    result.push(...getDescendants(child));
  });
  return result;
};

/**
 * 檢查用戶是否有權限訪問某個介面
 * @param {string[]} userInterfaces - 用戶擁有的介面權限列表
 * @param {string} interfaceKey - 要檢查的介面 key
 * @returns {boolean} 是否有權限
 */
export const hasInterfacePermission = (userInterfaces, interfaceKey) => {
  if (!userInterfaces || !Array.isArray(userInterfaces) || !interfaceKey) {
    return false;
  }

  // 展開權限（包含父子級關係）
  const expandedInterfaces = expandInterfacesWithChildren(userInterfaces);
  
  return expandedInterfaces.includes(interfaceKey);
};

/**
 * 遞歸過濾單一菜單項（含其巢狀子項），只保留有權限的節點
 */
function filterMenuItemRecursive(item, expandedInterfaces, userInterfaces, userInfo) {
  if (item.type === 'divider') {
    return item;
  }
  if (item.key === 'logout') {
    return item;
  }
  if (item.key === 'phoneVerificationAdmin' && !hasRole(userInfo, 'Tenant_Admin')) {
    return null;
  }
  if (item.key === 'reports.billingSystem' && !hasRole(userInfo, 'Tenant_Admin')) {
    return null;
  }
  if (item.key === 'admin.usageBilling.settings' && !hasRole(userInfo, 'Tenant_Admin')) {
    return null;
  }
  if (item.key === 'reports') {
    const hasAny = userInterfaces.some(iface => iface.startsWith('reports.'));
    const hasExpanded = expandedInterfaces.includes('reports');
    if (!hasAny && !hasExpanded) return null;
  } else if (!expandedInterfaces.includes(item.key)) {
    return null;
  }

  if (item.children && Array.isArray(item.children)) {
    const filteredChildren = item.children
      .map(child => filterMenuItemRecursive(child, expandedInterfaces, userInterfaces, userInfo))
      .filter(Boolean);
    if (filteredChildren.length === 0) {
      return null;
    }
    return { ...item, children: filteredChildren };
  }
  return item;
}

/**
 * 過濾菜單項（根據用戶權限）；巢狀子項會遞歸過濾（例如「商品管理」底下的子項只顯示有權限的）
 * @param {Array} menuItems - 原始菜單項列表
 * @param {string[]} userInterfaces - 用戶擁有的介面權限列表
 * @param {Object} userInfo - 用戶信息（可選，用於角色檢查）
 * @returns {Array} 過濾後的菜單項列表
 */
export const filterMenuItemsByPermission = (menuItems, userInterfaces, userInfo = null) => {
  if (!menuItems || !Array.isArray(menuItems) || !userInterfaces || !Array.isArray(userInterfaces)) {
    return [];
  }

  const expandedInterfaces = expandInterfacesWithChildren(userInterfaces);

  return menuItems
    .map(item => filterMenuItemRecursive(item, expandedInterfaces, userInterfaces, userInfo))
    .filter(item => item !== null);
};

/**
 * 獲取用戶的介面權限（從 API），供登入後選單／路由／功能權限使用。
 * 後端合約：GET /api/permissions/user/:userId 必須只回傳「該用戶在所屬公司下的角色權限」，
 * 不得包含「系統默認」(companyId=null) 的權限。系統默認僅用於權限管理頁在「新建公司時 copy 出初始化範本」。
 * @param {string} userId - 用戶 ID
 * @param {boolean} forceRefresh - 是否強制刷新（不使用緩存）
 * @param {string|null} [companyId] - 可選，用戶所屬公司 ID；若傳入則帶入查詢，後端應只回傳該公司權限
 * @returns {Promise<string[]>} 用戶的介面權限列表
 */
export const fetchUserInterfaces = async (userId, forceRefresh = false, companyId = null) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('無法獲取用戶權限：未登入');
      return [];
    }

    const params = new URLSearchParams();
    if (forceRefresh) params.set('t', String(Date.now()));
    if (companyId) params.set('companyId', companyId);
    const url = `/api/permissions/user/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      console.error('獲取用戶權限失敗:', response.status, response.statusText);
      const errorText = await response.text();
      console.error('錯誤詳情:', errorText);
      return [];
    }

    const data = await response.json();

    // 後端若回傳 interfaceSources / permissionSources，可 log 權限來源（公司 > 角色）
    const rawSources = data?.interfaceSources || data?.permissionSources || data?.data?.interfaceSources || data?.data?.permissionSources;
    if (rawSources && typeof rawSources === 'object') {
      WORKFLOW_MONITOR_KEYS.forEach(key => {
        const src = rawSources[key];
        if (src) {
          const list = Array.isArray(src) ? src : [src];
          console.log(`[PermissionUtils] ${key} 來自 API 回傳:`, list.map(s => `${s.companyName || s.companyId || '?'} > ${s.roleName || s.roleId || '?'}`).join('；'));
        }
      });
    }

    // 後端返回格式: { userId, interfaces: [...] }
    let rawInterfaces = [];
    
    if (Array.isArray(data)) {
      rawInterfaces = data;
    } else if (Array.isArray(data?.interfaces)) {
      rawInterfaces = data.interfaces;
    } else if (Array.isArray(data?.data?.interfaces)) {
      rawInterfaces = data.data.interfaces;
    } else if (Array.isArray(data?.data)) {
      rawInterfaces = data.data;
    } else {
      console.warn('[PermissionUtils] 無法識別的 API 返回格式:', data);
      return [];
    }

    // 統一整理為字串陣列
    const interfaces = rawInterfaces
      .map(item => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (!item || typeof item !== 'object') {
          return null;
        }
        const key = item.interfaceKey || item.interface_key || item.key || item.name || null;
        return key ? String(key).trim() : null;
      })
      .filter(Boolean);

    return interfaces;
  } catch (error) {
    console.error('[PermissionUtils] 獲取用戶權限錯誤:', error);
    return [];
  }
};

/**
 * 取得權限來源（公司 > 角色），用於除錯 log
 * 重要：登入後實際權限只來自「公司範圍」的角色，絕不使用「系統默認」。
 * 「系統默認」僅用於權限管理頁的「新建公司時 copy 出初始化範本」，不用於任何登入／選單／路由權限。
 * @param {string} userId - 用戶 ID
 * @param {string[]} interfaceKeys - 要查詢的權限 key（如 workflowMonitor.*）
 * @param {{ company_id?: string, companyId?: string } | null} [userInfo] - 可選，用戶資訊；若有 company_id 會用來解析「公司名稱 > 角色」
 * @returns {Promise<Object>} 各 key 的來源列表（僅公司範圍，不含系統默認）
 */
export const fetchPermissionSources = async (userId, interfaceKeys, userInfo = null) => {
  const token = localStorage.getItem('token');
  if (!token) return {};
  const needSources = interfaceKeys.filter(k => WORKFLOW_MONITOR_KEYS.includes(k));
  if (needSources.length === 0) return {};
  const userCompanyId = userInfo?.company_id ?? userInfo?.companyId ?? userInfo?.companyID ?? null;
  const sourcesByKey = {};
  needSources.forEach(k => { sourcesByKey[k] = []; });
  try {
    // 1. 使用者角色分配（可能含 companyId）
    const urRes = await fetch(`/api/roles/user/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!urRes.ok) return sourcesByKey;
    const urData = await urRes.json();
    let assignments = Array.isArray(urData) ? urData : (urData?.data ? (Array.isArray(urData.data) ? urData.data : [urData.data]) : []);
    // 若 API 只回傳 roleId 陣列，視為需用 userCompanyId 查公司範圍
    if (assignments.length > 0 && (typeof assignments[0] === 'string' || typeof assignments[0] === 'number')) {
      assignments = assignments.map(rid => ({ roleId: rid, companyId: null }));
    }
    if (assignments.length === 0) return sourcesByKey;

    // 2. 角色列表 id -> name
    const rolesRes = await fetch('/api/roles', { headers: { Authorization: `Bearer ${token}` } });
    const rolesList = rolesRes.ok ? (await rolesRes.json() || []) : [];
    const roleIdToName = Object.fromEntries(rolesList.map(r => [r.id, r.name || r.id]));

    // 3. 公司列表 id -> name
    let companyIdToName = {};
    try {
      const coRes = await fetch('/api/companies', { headers: { Authorization: `Bearer ${token}` } });
      if (coRes.ok) {
        const companies = await coRes.json();
        companyIdToName = Object.fromEntries((companies || []).map(c => [c.id, c.name || c.id]));
      }
    } catch (_) { /* ignore */ }

    const addSource = (key, companyId, companyName, roleId, roleName) => {
      sourcesByKey[key].push({ companyId: companyId ?? 'default', companyName, roleId, roleName });
    };

    // 4. 僅以「公司範圍」解析來源；絕不加入「系統默認」作為登入權限來源
    for (const a of assignments) {
      const roleId = a.roleId || a.role_id;
      let companyId = a.companyId ?? a.company_id ?? null;
      if (!roleId) continue;
      const roleName = roleIdToName[roleId] || roleId;

      if (companyId != null) {
        try {
          const url = `/api/permissions/role/${roleId}?companyId=${companyId}`;
          const prRes = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
          if (!prRes.ok) continue;
          const prData = await prRes.json();
          const roleIfaces = prData?.interfaces || (Array.isArray(prData) ? prData : []);
          const roleIfacesSet = new Set(roleIfaces.map(i => typeof i === 'string' ? i : (i?.interfaceKey || i?.interface_key || i?.key || i?.name)));
          const companyName = companyIdToName[companyId] || companyId;
          needSources.forEach(key => {
            if (roleIfacesSet.has(key)) addSource(key, companyId, companyName, roleId, roleName);
          });
        } catch (_) { /* skip */ }
        continue;
      }

      // 無 companyId：僅查用戶所屬公司範圍，不查系統默認（登入權限不得來自系統默認）
      if (userCompanyId) {
        try {
          const url = `/api/permissions/role/${roleId}?companyId=${userCompanyId}`;
          const prRes = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
          if (prRes.ok) {
            const prData = await prRes.json();
            const roleIfaces = prData?.interfaces || (Array.isArray(prData) ? prData : []);
            const roleIfacesSet = new Set(roleIfaces.map(i => typeof i === 'string' ? i : (i?.interfaceKey || i?.interface_key || i?.key || i?.name)));
            const companyName = companyIdToName[userCompanyId] || userCompanyId;
            needSources.forEach(key => {
              if (roleIfacesSet.has(key)) addSource(key, userCompanyId, companyName, roleId, roleName);
            });
          }
        } catch (_) { /* skip */ }
      }
    }
  } catch (e) {
    console.warn('[PermissionUtils] fetchPermissionSources 失敗:', e);
  }
  return sourcesByKey;
};

/**
 * 取得「該用戶在所屬公司下、由角色實際配置」的介面 key 集合（用於登入權限嚴格過濾）。
 * 只查該用戶的角色在 userCompanyId 下的權限，不包含系統默認。若後端多回傳了權限，前端只保留此集合內。
 * @param {string} userId - 用戶 ID
 * @param {string} userCompanyId - 用戶所屬公司 ID
 * @returns {Promise<Set<string>>} 該公司下該用戶角色已配置的 interface key 集合
 */
async function getAllowlistedInterfacesForUserCompany(userId, userCompanyId) {
  const token = localStorage.getItem('token');
  const allowlisted = new Set();
  if (!token || !userCompanyId) return allowlisted;
  try {
    const urRes = await fetch(`/api/roles/user/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!urRes.ok) return allowlisted;
    const urData = await urRes.json();
    let assignments = Array.isArray(urData) ? urData : (urData?.data ? (Array.isArray(urData.data) ? urData.data : [urData.data]) : []);
    if (assignments.length > 0 && (typeof assignments[0] === 'string' || typeof assignments[0] === 'number')) {
      assignments = assignments.map(rid => ({ roleId: rid, companyId: userCompanyId }));
    }
    for (const a of assignments) {
      const roleId = a.roleId || a.role_id;
      const companyId = a.companyId ?? a.company_id ?? userCompanyId;
      if (!roleId || companyId !== userCompanyId) continue;
      try {
        const url = `/api/permissions/role/${roleId}?companyId=${userCompanyId}`;
        const prRes = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!prRes.ok) continue;
        const prData = await prRes.json();
        const list = prData?.interfaces || (Array.isArray(prData) ? prData : []);
        list.forEach(i => {
          const k = typeof i === 'string' ? i : (i?.interfaceKey || i?.interface_key || i?.key || i?.name);
          if (k) allowlisted.add(k);
        });
      } catch (_) { /* skip */ }
    }
  } catch (e) {
    console.warn('[PermissionUtils] getAllowlistedInterfacesForUserCompany 失敗:', e);
  }
  return allowlisted;
}

/**
 * 從 API 獲取用戶權限（總是從後端獲取最新權限，不依賴 localStorage）。
 * 若有 company_id：僅保留「該用戶在所屬公司下、角色實際有配置」的權限，過濾掉可能來自系統默認或錯誤合併的權限。
 * @param {boolean} forceRefresh - 是否強制刷新（不使用緩存）
 * @returns {Promise<string[]>} 用戶的介面權限列表
 */
export const getUserInterfacesFromStorage = async (forceRefresh = false) => {
  try {
    const userInfoStr = localStorage.getItem('userInfo');
    if (!userInfoStr) {
      console.warn('[PermissionUtils] 無法獲取用戶信息：localStorage 中沒有 userInfo');
      return [];
    }

    const userInfo = JSON.parse(userInfoStr);
    const userId = userInfo.user_id || userInfo.userId || userInfo.id;
    const userCompanyId = userInfo.company_id || userInfo.companyId || userInfo.companyID || null;

    if (!userId) {
      return [];
    }

    // 傳入 companyId 讓後端只回傳該公司權限（若後端支援）；前端稍後再以 allowlist 嚴格過濾
    const interfacesFromApi = await fetchUserInterfaces(userId, forceRefresh, userCompanyId);

    if (!interfacesFromApi || interfacesFromApi.length === 0) {
      console.warn('[PermissionUtils] API 返回的權限列表為空，userId:', userId);
      return [];
    }

    // 登入權限嚴格以「所屬公司」為準：只保留在該公司下、角色實際有配置的 key，過濾掉系統默認或錯誤合併
    let effectiveInterfaces = interfacesFromApi;
    if (userCompanyId) {
      const allowlisted = await getAllowlistedInterfacesForUserCompany(userId, userCompanyId);
      if (allowlisted.size > 0) {
        effectiveInterfaces = interfacesFromApi.filter(k => allowlisted.has(k));
        const removed = interfacesFromApi.filter(k => !allowlisted.has(k));
        if (removed.length > 0) {
          console.warn('[PermissionUtils] 已依公司 ' + userCompanyId + ' 過濾掉非該公司配置的權限（登入不得使用系統默認）:', removed.slice(0, 20));
        }
      }
    }

    // 除錯：log workflowMonitor.* 權限來自哪個公司 > 哪個角色
    const hasWorkflowMonitorKeys = effectiveInterfaces.some(k => WORKFLOW_MONITOR_KEYS.includes(k));
    if (hasWorkflowMonitorKeys) {
      const monitorKeysInUser = effectiveInterfaces.filter(k => WORKFLOW_MONITOR_KEYS.includes(k));
      const sources = await fetchPermissionSources(userId, monitorKeysInUser, userInfo);
      WORKFLOW_MONITOR_KEYS.forEach(key => {
        if (!effectiveInterfaces.includes(key)) return;
        const list = sources[key] || [];
        if (list.length > 0) {
          console.log(`[PermissionUtils] ${key} 來自 公司>角色:`, list.map(s => `${s.companyName} > ${s.roleName}`).join('；'));
        } else {
          console.log(`[PermissionUtils] ${key} 來自 公司>角色: (無法解析來源，可能為後端合併結果或 API 未回傳角色分配)`);
        }
      });
    }

    if (userInfo) {
      userInfo.interfaces = effectiveInterfaces;
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
    }

    return effectiveInterfaces;
  } catch (error) {
    console.error('[PermissionUtils] 獲取用戶權限錯誤:', error);
    return [];
  }
};

/**
 * 檢查用戶是否有特定角色
 * @param {Object} userInfo - 用戶信息對象
 * @param {string} roleName - 角色名稱（如 'Tenant_Admin', 'Company_Admin' 等）
 * @returns {boolean} 是否有該角色
 */
export const hasRole = (userInfo, roleName) => {
  if (!userInfo || !userInfo.roles || !Array.isArray(userInfo.roles)) {
    return false;
  }

  return userInfo.roles.some(role => {
    if (typeof role === 'string') {
      return role === roleName;
    }
    if (typeof role === 'object' && role.name) {
      return role.name === roleName;
    }
    return false;
  });
};

/**
 * 檢查用戶是否為 Tenant_Admin 或 Company_Admin（用於權限管理頁面）
 * @param {Object} userInfo - 用戶信息對象
 * @returns {boolean} 是否有權限訪問權限管理頁面
 */
export const canAccessPermissionManagement = (userInfo) => {
  return hasRole(userInfo, 'Tenant_Admin') || hasRole(userInfo, 'Company_Admin');
};

/**
 * 檢查用戶是否為 Tenant_Admin（用於電話號碼驗證管理頁面）
 * @param {Object} userInfo - 用戶信息對象
 * @returns {boolean} 是否有權限訪問電話號碼驗證管理頁面
 */
export const canAccessPhoneVerificationAdmin = (userInfo) => {
  return hasRole(userInfo, 'Tenant_Admin');
};

/**
 * 登入使用者主屬公司 id（localStorage `userInfo`），供 Commerce 等頁公司篩選預設值。
 * @returns {string|undefined}
 */
export function getUserPrimaryCompanyId() {
  if (typeof window === 'undefined') return undefined;
  try {
    const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const id = u.company_id ?? u.companyId ?? u.companyID;
    if (id == null || id === '') return undefined;
    return String(id);
  } catch {
    return undefined;
  }
}
